import { useEffect, useRef, useState } from "react";
import { Box, Card, Typography } from "@mui/material";

// ✅ Dummy Data (no IMAGE_BASE_URL needed for testing)
const dummyProducts = [
  {
    itemName: "Cold Coffee",
    itemDescription: "Refreshing cold coffee with ice cream and chocolate syrup",
    imageUrl: "https://images.unsplash.com/photo-1517701604599-bb29b565090c",
  },
  {
    itemName: "Veg Sandwich",
    itemDescription: "Healthy sandwich with fresh veggies and cheese",
    imageUrl: "https://images.unsplash.com/photo-1528735602780-2552fd46c7af",
  },
  {
    itemName: "Burger",
    itemDescription: "Juicy burger with crispy patty and cheese slice",
    imageUrl: "https://images.unsplash.com/photo-1550547660-d9450f859349",
  },
  {
    itemName: "Pizza",
    itemDescription: "Delicious cheesy pizza with toppings",
    imageUrl: "https://images.unsplash.com/photo-1548365328-9f547fb0953d",
  },
  {
    itemName: "French Fries",
    itemDescription: "Crispy golden fries with peri peri masala",
    imageUrl: "https://images.unsplash.com/photo-1541592106381-b31e9677c0e5",
  },
];

const ProductCarousel = ({ products = dummyProducts }) => {
  const scrollRef = useRef(null);
  const animationRef = useRef(null);
  const isPausedRef = useRef(false);
  const [isPaused, setIsPaused] = useState(false);

  // Sync ref with state
  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  const loopProducts = [
    ...(products || []),
    ...(products || []),
    ...(products || []),
  ];

  useEffect(() => {
    const container = scrollRef.current;
    if (!container || !products?.length) return;

    const speed = 0.5;

    const init = () => {
      const oneSetWidth = container.scrollWidth / 3;
      container.scrollLeft = oneSetWidth;

      const autoScroll = () => {
        if (!isPausedRef.current) {
          container.scrollLeft += speed;

          const oneSet = container.scrollWidth / 3;

          if (container.scrollLeft >= oneSet * 2) {
            container.scrollLeft = oneSet;
          }

          if (container.scrollLeft <= 0) {
            container.scrollLeft = oneSet;
          }
        }

        animationRef.current = requestAnimationFrame(autoScroll);
      };

      animationRef.current = requestAnimationFrame(autoScroll);
    };

    const rafId = requestAnimationFrame(init);

    return () => {
      cancelAnimationFrame(rafId);
      cancelAnimationFrame(animationRef.current);
    };
  }, [products]);

  // Touch handlers
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const onTouchStart = () => {
      isPausedRef.current = true;
      setIsPaused(true);
    };

    const onTouchEnd = () => {
      setTimeout(() => {
        isPausedRef.current = false;
        setIsPaused(false);
      }, 1200);
    };

    container.addEventListener("touchstart", onTouchStart, { passive: true });
    container.addEventListener("touchend", onTouchEnd, { passive: true });

    return () => {
      container.removeEventListener("touchstart", onTouchStart);
      container.removeEventListener("touchend", onTouchEnd);
    };
  }, []);

  return (
    <Box
      ref={scrollRef}
      onMouseEnter={() => {
        isPausedRef.current = true;
        setIsPaused(true);
      }}
      onMouseLeave={() => {
        isPausedRef.current = false;
        setIsPaused(false);
      }}
      sx={{
        display: "flex",
        gap: 2,
        overflowX: "scroll",
        mt: 2,
        pb: 1,
        scrollBehavior: "auto",
        WebkitOverflowScrolling: "touch",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      {loopProducts.map((item, index) => (
        <Card
          key={index}
          sx={{
            minWidth: { xs: products?.length > 1 ? "85%" : "100%", sm: 260 },
            flexShrink: 0,
            borderRadius: 3,
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                overflow: "hidden",
                bgcolor: "#f3f4f6",
              }}
            >
              <img
                src={item.imageUrl}
                alt={item.itemName}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </Box>

            <Box>
              <Typography fontWeight={700} fontSize={15}>
                {item.itemName}
              </Typography>

              <Typography fontSize={12} color="text.secondary">
                {item.itemDescription?.length > 40
                  ? item.itemDescription.slice(0, 40) + "..."
                  : item.itemDescription}
              </Typography>
            </Box>
          </Box>
        </Card>
      ))}
    </Box>
  );
};

export default ProductCarousel;